import React from "react";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Expand } from "lucide-react";
import "./index.css";

interface InfoPanelProps {
  pageRoute?: string;
}

const InfoPanel: React.FC<InfoPanelProps> = ({ pageRoute: propPageRoute }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getPageConfig, getPageRoutes, getPageMetadata, appName, showNavigation } = useAppConfig();

  // Extract page route from location pathname (remove leading slash)
  const pathPageRoute =
    location.pathname === "/" ? "home" : location.pathname.substring(1);

  // Use prop if provided, otherwise use path, fallback to 'home'
  const currentPageRoute = propPageRoute || pathPageRoute || "home";

  // Get page-specific configuration
  const pageConfig = getPageConfig(currentPageRoute);

  // Get all available page routes
  const pageRoutes = getPageRoutes();

  // Get all info sections from config, fallback to defaults
  const infoSections = pageConfig?.infoList || [
    {
      title: "Data Sources",
      list: [
        {
          header: "ACES PCdb",
          context:
            "The ultimate parts library. If it's got a part, we know it.",
        },
        {
          header: "Predii Taxonomy",
          context:
            "Handpicked by auto experts, so you're not just getting data - you're getting wisdom on wheels.",
        },
        {
          header: "VMRS",
          context:
            "The maintenance playbook that keeps fleets running. Standard codes that make sense of every repair.",
        },
      ],
    },
  ];

  const handleExpandClick = () => {
    navigate(`/info-panel-expanded?page=${currentPageRoute}`);
  };

  return (
    <aside className="info-panel">
      <div className="info-panel__header">
        <div className="info-panel__logo">
          <img src="/plain_logo.png" alt="Predii Logo" className="logo-image" />
        </div>
        <div className="info-panel__brand">
          <h2 className="brand-name">{appName}</h2>
          <p className="brand-tagline">
            {pageConfig?.agentLabel || "Parts Agent"}
          </p>
        </div>
        <button 
          className="info-panel__expand-btn"
          onClick={handleExpandClick}
          aria-label="Expand info panel"
        >
          <Expand size={16} />
        </button>
      </div>

      <div className="info-panel__content">
        <div className="info-panel__section">
          <h3 className="info-panel__title">
            {pageConfig?.infoHeader ||
              "Your AI parts counter assistant powered by Predii Agent."}
          </h3>
          <p className="info-panel__desc">
            {pageConfig?.infoSubHeader ||
              'Finds parts the way you ask for them — understands shop talk, fixes typos, and knows that "brake shoes" and "brake pads" aren\'t always the same thing.'}
          </p>
        </div>

        {/* Compact Navigation Selector */}
        {showNavigation && (
          <div className="info-panel__nav-selector">
            <div className="nav-selector">
              <div className="nav-selector__label">Current Page</div>
              <div className="nav-selector__dropdown">
                <div className="nav-selector__current">
                  <span className="nav-selector__current-text">
                    {getPageMetadata(currentPageRoute)?.name || currentPageRoute}
                  </span>
                  <svg className="nav-selector__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m6 9 6 6 6-6"/>
                  </svg>
                </div>
                <div className="nav-selector__menu">
                  {pageRoutes.map((route) => {
                    const pageMetadata = getPageMetadata(route);
                    const isActive = route === currentPageRoute;
                    return (
                      <Link
                        key={route}
                        to={`/${route === 'home' ? '' : route}`}
                        className={`nav-selector__option ${isActive ? 'nav-selector__option--active' : ''}`}
                      >
                        <span className="nav-selector__option-text">
                          {pageMetadata?.name || route}
                        </span>
                        {isActive && (
                          <div className="nav-selector__check">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="m9 12 2 2 4-4"/>
                            </svg>
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {infoSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="info-panel__meta-card">
            <div className="meta-item">
              <div className="meta-label">{section.title}</div>
              <ul className="meta-list">
                {section.list.map((item, index) => (
                  <li key={index}>
                    <span className="source-name">{item.header}:</span>{" "}
                    {item.context}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default InfoPanel;
